import useAccount from '../useAccount';
import { useEffect } from 'react';
import { SESSION_CREATION } from '@/constants/localStorage';

const TIMERS: number[] = [];

const useLogoutWarning = () => {
  const { account, loggedIn } = useAccount();
  const tokenCreationMS = account?.tokenCreation;

  const tokenCreationFromStorage: number | undefined =
    typeof window !== 'undefined'
      ? Number(localStorage.getItem(SESSION_CREATION)) || undefined
      : undefined;

  const checkAndLogoutUser = (tokenCreationTime?: number, expiryTimeStamp?: number): void => {
    const tokenCreation = tokenCreationTime || (tokenCreationMS as number);
    if (!expiryTimeStamp) return;

    if (Date.now() >= expiryTimeStamp && tokenCreation < expiryTimeStamp) {
      window.dispatchEvent(new CustomEvent<{ navigate: Boolean }>('LOGOUT_USER', { detail: { navigate: false } }));
    }
  };

  const clearTimers = () => {
    if (TIMERS.length) {
      TIMERS.forEach((timerId) => {
        clearTimeout(timerId);
      });
      TIMERS.length = 0;
    }
  };

  const getLogoutExpiryTime = (_tokenCreation?: number): number | undefined => {
    if (!tokenCreationFromStorage) return undefined;

    const expiryDate = new Date(tokenCreationFromStorage);
    expiryDate.setDate(expiryDate.getDate() + 25);
    expiryDate.setHours(0, 0, 0, 0);
    return expiryDate.getTime();
  };

  const scheduleCheckAtMidnight = (tokenCreation?: number) => {
    clearTimers();
    const currentTimeStamp = Date.now();

    const expiryTimeMs = getLogoutExpiryTime(tokenCreation);

    if (!expiryTimeMs) {
      return;
    }

    const timeToExpire = expiryTimeMs > currentTimeStamp ? expiryTimeMs - currentTimeStamp : 0;

    if (timeToExpire <= 0) {
      checkAndLogoutUser(tokenCreation, expiryTimeMs);
      return;
    }

    const timerValueInSeconds: number = process.env.NEXT_PUBLIC_LOGOUT_WARNING_SEC
      ? parseInt(process.env.NEXT_PUBLIC_LOGOUT_WARNING_SEC)
      : 1 * 60;
    const timerValueInMS = timerValueInSeconds * 1000;
    const plentyOfTimeLeft = timeToExpire - timerValueInMS > 0;
    const showTimerWaitTime = plentyOfTimeLeft ? timeToExpire - timerValueInMS : 2000;
    const actualTimerValueInSeconds = plentyOfTimeLeft
      ? timerValueInSeconds
      : Math.ceil(timeToExpire / 1000) - showTimerWaitTime / 1000;

    const warningTimeoutId: number = window.setTimeout(() => {
      const event = new CustomEvent('SHOW_LOGOUT_WARNING', {
        detail: { countDownTimeSec: actualTimerValueInSeconds },
      });
      window.dispatchEvent(event);
    }, showTimerWaitTime);

    const logoutTimeOutId: number = window.setTimeout(() => {
      checkAndLogoutUser(tokenCreation, expiryTimeMs);
    }, timeToExpire);

    TIMERS.push(warningTimeoutId);
    TIMERS.push(logoutTimeOutId);
  };

  useEffect(() => {
    if (loggedIn) {
      scheduleCheckAtMidnight(tokenCreationMS);
    } else {
      clearTimers();
    }
  }, [loggedIn, tokenCreationMS, tokenCreationFromStorage]);
};

export default useLogoutWarning;
